import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Mail, ArrowLeft, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../config/api';

const EmailVerification = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [email, setEmail] = useState('');
    const [countdown, setCountdown] = useState(0);
    const [accountType, setAccountType] = useState('profile');

    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm();

    useEffect(() => {
        // Get email from location state or query params
        const emailFromState = location.state?.email;
        const accountTypeFromState = location.state?.accountType;
        const emailFromParams = new URLSearchParams(location.search).get('email');
        const emailToUse = emailFromState || emailFromParams;

        if (emailToUse) {
            setEmail(emailToUse);
            if (accountTypeFromState) setAccountType(accountTypeFromState);
        } else {
            // Redirect to register if no email
            navigate('/register');
        }
    }, [location, navigate]);

    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [countdown]);

    const onSubmit = async (data) => {
        setIsLoading(true);
        try {
            // Server supports POST /api/auth/verify (and alias /verify-email)
            const response = await api.post('/api/auth/verify', {
                email,
                code: data.code
            });

            const { token } = response.data || {};
            if (token) {
                localStorage.setItem('token', token);
            }

            toast.success('Email успешно подтвержден!');

            // Redirect to success page (so user sees confirmation and can login)
            navigate('/verify-email/success', {
                state: { email, accountType },
                replace: true
            });

        } catch (error) {
            const message = error.response?.data?.error || 'Ошибка при подтверждении email';
            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendCode = async () => {
        setIsResending(true);
        try {
            await api.post('/api/auth/resend-verification', { email });
            toast.success('Новый код отправлен на ваш email');
            setCountdown(60); // 60 seconds cooldown
        } catch (error) {
            const message = error.response?.data?.error || 'Ошибка при отправке кода';
            toast.error(message);
        } finally {
            setIsResending(false);
        }
    };

    return (
        <div className="min-h-screen bg-secondary-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div className="text-center">
                    <div className="flex justify-center mb-6">
                        <div className="w-12 h-12 bg-primary-500 rounded-lg flex items-center justify-center">
                            <span className="text-white font-bold text-xl">a</span>
                        </div>
                    </div>
                    <h2 className="text-3xl font-bold text-secondary-900">
                        Подтверждение email
                    </h2>
                    <p className="mt-2 text-secondary-600">
                        Мы отправили код подтверждения на {email}
                    </p>
                </div>

                <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="code" className="block text-sm font-medium text-secondary-700 mb-2">
                                Код подтверждения
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400 w-5 h-5" />
                                <input
                                    id="code"
                                    type="text"
                                    {...register('code', {
                                        required: 'Код подтверждения обязателен',
                                        pattern: {
                                            value: /^[0-9]{6}$/,
                                            message: 'Код должен содержать 6 цифр'
                                        }
                                    })}
                                    className="input pl-10 text-center text-2xl font-mono tracking-widest"
                                    placeholder="000000"
                                    maxLength={6}
                                    autoComplete="off"
                                />
                            </div>
                            {errors.code && (
                                <p className="mt-1 text-sm text-orange-600">{errors.code.message}</p>
                            )}
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={isLoading}
                        className="btn btn-primary w-full flex items-center justify-center"
                    >
                        {isLoading ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                Подтверждение...
                            </>
                        ) : (
                            'Подтвердить email'
                        )}
                    </button>
                </form>

                <div className="space-y-4">
                    <div className="text-center">
                        <p className="text-sm text-secondary-600 mb-2">
                            Не получили код?
                        </p>
                        <button
                            onClick={handleResendCode}
                            disabled={isResending || countdown > 0}
                            className="text-primary-600 hover:text-primary-500 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center mx-auto"
                        >
                            {isResending ? (
                                <>
                                    <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                                    Отправка...
                                </>
                            ) : countdown > 0 ? (
                                `Отправить снова через ${countdown}с`
                            ) : (
                                <>
                                    <RefreshCw className="w-4 h-4 mr-1" />
                                    Отправить код снова
                                </>
                            )}
                        </button>
                    </div>

                    <div className="text-center">
                        <button
                            onClick={() => navigate('/register')}
                            className="text-secondary-600 hover:text-secondary-500 font-medium text-sm flex items-center justify-center mx-auto"
                        >
                            <ArrowLeft className="w-4 h-4 mr-1" />
                            Вернуться к регистрации
                        </button>
                    </div>
                </div>

                <div className="mt-8 p-4 bg-primary-50 rounded-lg border border-primary-100">
                    <h3 className="text-sm font-medium text-secondary-900 mb-2">
                        Что делать дальше?
                    </h3>
                    <ul className="text-sm text-secondary-700 space-y-1">
                        <li>• Проверьте папку "Входящие" в вашем email</li>
                        <li>• Проверьте папку "Спам" если письма нет</li>
                        <li>• Введите 6-значный код из письма</li>
                        <li>• Нажмите "Подтвердить email"</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default EmailVerification;
